import {
  CareOrderStatusEnum,
  IconName,
  filterExistsAndExcludes,
  useGenericFilter,
  useSidesFilter,
  useSymbolsFilter,
  type BlotterTableClientLocalFilter,
  type CareOrder,
  type DateRangeFilter,
  type FilterableProperty,
  type GenericFilterFilterProps,
} from '@talos/kyoko';
import { startCase, values } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { passesCareOrderDateRangeFilter } from '../careOrderDateRangeFilter';
import type { CareOrderTimestampField } from '../careOrderTimestampFields';

interface CareOrderManagementFilter extends DateRangeFilter<CareOrderTimestampField> {
  Statuses?: string[];
  Symbols?: string[];
  Sides?: string[];
  OriginationTrader?: string[];
}

interface UseCareOrderManagementFiltersParams {
  filterProps: GenericFilterFilterProps<CareOrderManagementFilter>;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export const useCareOrderManagementFilters = ({
  filterProps,
  includeOpenCareOrdersOutsideDateRange = true,
}: UseCareOrderManagementFiltersParams) => {
  const sidesFilter = useSidesFilter();
  const symbolsFilter = useSymbolsFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () => [
      sidesFilter,
      {
        key: 'Statuses',
        label: 'Status',
        icon: IconName.CheckCircle,
        options: values(CareOrderStatusEnum),
        getOptionLabel: startCase,
      },
      {
        key: 'OriginationTrader',
        label: 'Origination Trader',
        icon: IconName.User,
        control: 'text',
      },
      symbolsFilter,
    ],
    [sidesFilter, symbolsFilter]
  );

  const genericFilterResults = useGenericFilter<CareOrderManagementFilter>(filterableProperties, filterProps);

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<CareOrder>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }

      if (!passesCareOrderDateRangeFilter(data, genericFilterResults.filter, includeOpenCareOrdersOutsideDateRange)) {
        return false;
      }

      if (
        filterExistsAndExcludes(genericFilterResults.filter as CareOrderManagementFilter, 'Statuses', data, 'status')
      ) {
        return false;
      }

      if (
        filterExistsAndExcludes(genericFilterResults.filter as CareOrderManagementFilter, 'Symbols', data, 'Symbol')
      ) {
        return false;
      }

      if (filterExistsAndExcludes(genericFilterResults.filter as CareOrderManagementFilter, 'Sides', data, 'Side')) {
        return false;
      }

      if (
        filterExistsAndExcludes(
          genericFilterResults.filter as CareOrderManagementFilter,
          'OriginationTrader',
          data,
          'originationTrader'
        )
      ) {
        return false;
      }

      return true;
    },
    [genericFilterResults.filter, includeOpenCareOrdersOutsideDateRange]
  );

  return { ...genericFilterResults, clientSideFilter };
};
